import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { ChevronDownIcon } from '../../icons/ChevronDownIcon';
import { truncateString } from '../../util/string';

export type SelectDropdownOption = {
  text: string;
  value: string;
};

export type SelectDropdownProps = {
  options: SelectDropdownOption[];
  value?: string;
  placeholder?: string;
  reflectParentState?: boolean;
  onChange: (selected: string) => void;
  className?: string;
  buttonClassName?: string;
  labelClassName?: string;
  selectedClassName?: string;
  dropdownClassName?: string;
  optionClassName?: string;
  iconFill?: string;
};

const SelectDropdown = ({
  options,
  value,
  placeholder = 'Select option',
  onChange,
  reflectParentState = false,
  className,
  buttonClassName,
  labelClassName,
  selectedClassName,
  dropdownClassName,
  optionClassName,
  iconFill = 'currentColor',
  ...props
}: SelectDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<string | undefined>(value);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleOptionSelect = (optionValue: string) => {
    setSelected(optionValue);
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (!dropdownRef.current?.contains(e.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (reflectParentState || value !== undefined) {
      setSelected(value);
    }
  }, [reflectParentState, value]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === selected);
  const displayLabel = selectedOption ? selectedOption.text : null;

  return (
    <div className={classNames('relative content-center text-left', className)} ref={dropdownRef} {...props}>
      <div
        className={classNames(
          'border-gray-300 hover:bg-gray-50 flex h-10 w-full flex-row items-center gap-x-2 border px-4 text-sm font-medium',
          buttonClassName
        )}
      >
        <button type="button" className="flex w-full flex-row items-center gap-x-2" onClick={toggleDropdown}>
          <p className={classNames('min-w-0 flex-1 truncate text-left font-bold', labelClassName, selectedClassName)}>
            {displayLabel ? truncateString(displayLabel, 40) : placeholder}
          </p>
          <span className="flex-shrink-0">
            <ChevronDownIcon
              width={20}
              height={20}
              className={iconFill.startsWith('#') ? `text-[${iconFill}]` : `text-${iconFill}`}
            />
          </span>
        </button>
      </div>
      {isOpen && (
        <div
          className={classNames('absolute z-10 w-full rounded-md border bg-white shadow-lg', dropdownClassName)}
          style={{
            borderColor: '#E7E9E9',
          }}
        >
          <ul className="max-h-60 list-none overflow-y-auto">
            {options.map((option) => (
              <li
                key={option.value}
                onClick={() => handleOptionSelect(option.value)}
                className={classNames('cursor-pointer rounded-md p-1 hover:bg-slate-1', optionClassName)}
              >
                <p className="cursor-pointer">{option.text}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SelectDropdown;
